import ExportButton from 'components/ExportButton/ExportButton';
import {
  type Dispatch,
  FC,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Plus } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { type ReduxState } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { createCSVFileName, stripHtml } from 'utils/util/util';
import ThreadedCommentComposer from './ThreadedCommentComposer';
import ThreadedCommentFilters from './ThreadedCommentFilters';
import ThreadedCommentItem from './ThreadedCommentItem';
import {
  buildThreadedCommentTree,
  filterPrivateThreadedComments,
  type ThreadedComment,
  type ThreadedCommentNode,
} from './threadedComments';
import { threadedCommentsApi } from './threadedCommentsApi';

export type ThreadedCommentsVariant = 'modal' | 'popover';

interface Props {
  comments: ThreadedComment[];
  onCommentsChange: Dispatch<SetStateAction<ThreadedComment[]>>;
  contentType: number;
  objectId: number;
  addCommentLabel?: React.ReactNode;
  /** Whether the current viewer is allowed to add a comment (or reply) on this object. */
  canComment?: boolean;
  variant: ThreadedCommentsVariant;
  onComposerContentChange?: (value: string) => void;
  /**
   * Called with the server's up-to-date public comment count whenever a
   * mutation (new comment/reply, privacy toggle) could have changed it.
   */
  onPublicCommentCountChange?: (count: number) => void;
}

const ThreadedCommentsContent: FC<Props> = ({
  comments,
  onCommentsChange,
  contentType,
  objectId,
  addCommentLabel,
  canComment = false,
  variant,
  onComposerContentChange,
  onPublicCommentCountChange,
}) => {
  const { formatMessage } = useIntl();
  const proxyParameters = useProxyParameters();
  const isModal = variant === 'modal';

  const currentUserId = useSelector((state: ReduxState) => {
    const person = state.currentProxyPerson || state.me;
    return person?.id;
  });

  const tree = useMemo(() => buildThreadedCommentTree(comments), [comments]);

  const [showPrivateComments, setShowPrivateComments] = useState(false);
  const filteredTree = useMemo(
    () => filterPrivateThreadedComments(tree, showPrivateComments),
    [tree, showPrivateComments]
  );

  const [expandedThreads, setExpandedThreads] = useState<Set<number>>(
    () => new Set()
  );
  const rootThreadIds = useMemo(
    () => filteredTree.map((comment) => comment.id),
    [filteredTree]
  );
  const allThreadsExpanded = useMemo(
    () =>
      rootThreadIds.length > 0 &&
      rootThreadIds.every((id) => expandedThreads.has(id)),
    [rootThreadIds, expandedThreads]
  );

  const onToggleThread = useCallback((rootId: number) => {
    setExpandedThreads((current) => {
      const next = new Set(current);
      if (next.has(rootId)) {
        next.delete(rootId);
      } else {
        next.add(rootId);
      }
      return next;
    });
  }, []);

  const handleToggleExpandCollapse = useCallback(() => {
    setExpandedThreads((current) => {
      if (allThreadsExpanded) {
        return new Set();
      }
      const next = new Set(current);
      rootThreadIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allThreadsExpanded, rootThreadIds]);

  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerContent, setComposerContentState] = useState('');
  const [composerIsPrivate, setComposerIsPrivate] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [isSubmittingComposer, setIsSubmittingComposer] = useState(false);

  const setComposerContent = useCallback(
    (value: string) => {
      setComposerContentState(value);
      onComposerContentChange?.(value);
    },
    [onComposerContentChange]
  );

  const [activeReplyTarget, setActiveReplyTarget] = useState<
    number | undefined
  >(undefined);
  const [replyText, setReplyText] = useState('');
  const [replyIsPrivate, setReplyIsPrivate] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const genericErrorMessage = formatMessage({
    id: 'app.views.widgets.comments.threaded_comments_modal.error.generic',
    defaultMessage: 'Unable to save the comment. Please try again.',
  });

  const emptyErrorMessage = formatMessage({
    id: 'app.views.widgets.comments.threaded_comments_modal.error.empty',
    defaultMessage: 'Please enter a comment before saving.',
  });

  const openComposer = useCallback(() => {
    setIsComposerOpen(true);
    setComposerContent('');
    setComposerIsPrivate(false);
    setComposerError(null);
  }, [setComposerContent]);

  const closeComposer = useCallback(() => {
    setIsComposerOpen(false);
    setComposerContent('');
    setComposerIsPrivate(false);
    setComposerError(null);
  }, [setComposerContent]);

  const submitComment = useCallback(
    (
      content: string,
      isPrivate: boolean,
      setSubmitting: (value: boolean) => void,
      setError: (value: string | null) => void,
      onSuccess: () => void,
      parentId?: number
    ) => {
      const plainContent = stripHtml(content ?? '', true).trim();
      if (!plainContent) {
        setError(emptyErrorMessage);
        return;
      }

      setSubmitting(true);
      setError(null);

      threadedCommentsApi.createComment(
        proxyParameters,
        {
          content_type: contentType,
          object_id: objectId,
          body: content,
          is_private: isPrivate,
          ...(parentId !== undefined && { parent_comment: parentId }),
        },
        ({ public_comment_count, ...newComment }) => {
          onCommentsChange((prev) => [...prev, newComment]);
          onPublicCommentCountChange?.(public_comment_count);
          setSubmitting(false);
          onSuccess();
        },
        (error) => {
          setError(typeof error === 'string' ? error : genericErrorMessage);
          setSubmitting(false);
        }
      );
    },
    [
      contentType,
      objectId,
      proxyParameters,
      onPublicCommentCountChange,
      emptyErrorMessage,
      genericErrorMessage,
      onCommentsChange,
    ]
  );

  const handleComposerSubmit = useCallback(
    () =>
      submitComment(
        composerContent,
        composerIsPrivate,
        setIsSubmittingComposer,
        setComposerError,
        closeComposer
      ),
    [submitComment, composerContent, composerIsPrivate, closeComposer]
  );

  // Opens the reply box for a comment when given its id, closes whichever
  // reply box is open when called with none.
  const toggleReplyComposer = useCallback((commentId?: number) => {
    setActiveReplyTarget(commentId);
    setReplyText('');
    setReplyIsPrivate(false);
    setReplyError(null);
  }, []);

  const handleSubmitReply = useCallback(
    (parentId: number) =>
      submitComment(
        replyText,
        replyIsPrivate,
        setIsSubmittingReply,
        setReplyError,
        toggleReplyComposer,
        parentId
      ),
    [submitComment, replyText, replyIsPrivate, toggleReplyComposer]
  );

  const [privacyToggleError, setPrivacyToggleError] = useState<string | null>(
    null
  );

  const handleToggleIsPrivate = useCallback(
    (comment: ThreadedCommentNode) => {
      setPrivacyToggleError(null);
      threadedCommentsApi.updateIsPrivate(
        comment.id,
        !comment.is_private,
        proxyParameters,
        ({ public_comment_count, ...updatedComment }) => {
          onCommentsChange((prev) =>
            prev.map((c) => (c.id === updatedComment.id ? updatedComment : c))
          );
          onPublicCommentCountChange?.(public_comment_count);
        },
        (error) => {
          setPrivacyToggleError(
            typeof error === 'string' ? error : genericErrorMessage
          );
        }
      );
    },
    [
      proxyParameters,
      onCommentsChange,
      onPublicCommentCountChange,
      genericErrorMessage,
    ]
  );

  const csvFilename = createCSVFileName('survey-answer-comments');

  const csvData = useMemo(() => {
    if (!isModal || comments.length === 0) {
      return [];
    }

    const header = [
      'Comment ID',
      'Parent Comment ID',
      'Author',
      'Author Title',
      'Author Email',
      'Private',
      'Created At',
      'Updated At',
      'Comment',
    ];

    const rows = [...comments]
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      .map((comment) => {
        const authorName =
          comment.author?.full_name ??
          formatMessage({
            id: 'app.views.widgets.comments.threaded_comments_modal.author.unknown',
            defaultMessage: 'Someone',
          });
        return [
          comment.id?.toString() ?? '',
          comment.parent_comment?.toString() ?? '',
          authorName,
          comment.author?.title ?? '',
          comment.author?.email ?? '',
          comment.is_private ? 'Yes' : 'No',
          comment.created_at ?? '',
          comment.updated_at ?? '',
          comment.body ?? '',
        ];
      });

    return [header, ...rows];
  }, [comments, formatMessage, isModal]);

  const containerClassName = ['p-3', isModal ? 'w-100' : '']
    .filter(Boolean)
    .join(' ');
  const containerStyle = isModal
    ? undefined
    : { minWidth: '22rem', maxWidth: '28rem' };

  return (
    <div className={containerClassName} style={containerStyle}>
      {isModal && tree.length > 0 && (
        <ThreadedCommentFilters
          toggleId={`threaded-comments-show-private-${contentType}-${objectId}`}
          showPrivateComments={showPrivateComments}
          onToggleShowPrivate={setShowPrivateComments}
          allThreadsExpanded={allThreadsExpanded}
          onToggleExpandCollapse={handleToggleExpandCollapse}
          formatMessage={formatMessage}
        >
          <ExportButton filename={csvFilename} data={csvData} />
        </ThreadedCommentFilters>
      )}
      {privacyToggleError && (
        <div className="text-danger small mb-2">{privacyToggleError}</div>
      )}
      {filteredTree.length === 0 ? (
        <div className="small text-muted">
          <FormattedMessage
            id="app.views.widgets.comments.threaded_comments_modal.empty"
            defaultMessage="No comments yet."
          />
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {filteredTree.map((comment) => (
            <ThreadedCommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              rootId={comment.id}
              isThreadExpanded={expandedThreads.has(comment.id)}
              onToggleThread={onToggleThread}
              canReply={isModal && canComment}
              activeReplyTarget={activeReplyTarget}
              onOpenReply={toggleReplyComposer}
              onCloseReply={toggleReplyComposer}
              replyText={replyText}
              onReplyTextChange={setReplyText}
              replyIsPrivate={replyIsPrivate}
              onReplyIsPrivateChange={setReplyIsPrivate}
              replyError={replyError}
              isSubmittingReply={isSubmittingReply}
              onSubmitReply={handleSubmitReply}
              formatMessage={formatMessage}
              currentUserId={currentUserId}
              onToggleIsPrivate={isModal ? handleToggleIsPrivate : undefined}
              // the popover scrolls, so collapsing long comments is not needed
              showFullBody={!isModal}
            />
          ))}
        </div>
      )}
      {isModal && canComment && (
        <div className="mt-3">
          {isComposerOpen ? (
            <ThreadedCommentComposer
              editorName="threaded-comment-composer"
              content={composerContent}
              onContentChange={setComposerContent}
              isPrivate={composerIsPrivate}
              onIsPrivateChange={setComposerIsPrivate}
              error={composerError}
              isSubmitting={isSubmittingComposer}
              onSubmit={handleComposerSubmit}
              onCancel={closeComposer}
              submitLabel={
                <FormattedMessage
                  id="app.views.widgets.comments.threaded_comments_modal.submit_comment"
                  defaultMessage="Post comment"
                />
              }
              label={
                addCommentLabel ?? (
                  <FormattedMessage
                    id="app.views.widgets.comments.threaded_comments_modal.add_comment_label"
                    defaultMessage="Add a comment"
                  />
                )
              }
              confirmDiscardOnCancel
              autoFocus
            />
          ) : (
            <Button
              color="secondary"
              outline
              size="sm"
              type="button"
              className="d-inline-flex align-items-center gap-2"
              onClick={openComposer}
            >
              <Plus size={16} aria-hidden="true" />
              <FormattedMessage
                id="app.views.widgets.comments.threaded_comments_modal.add_comment"
                defaultMessage="Add comment"
              />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ThreadedCommentsContent;
